import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'react-feather';
import styles from './App.module.css';
import HandleSelection from '../HandleSelection/HandleSelection';
import ConnectionStatus from '../ConnectionStatus/ConnectionStatus';
import ChannelList from '../ChannelList/ChannelList';
import UserList from '../UserList/UserList';
import MessageArea from '../MessageArea/MessageArea';
import InputArea from '../InputArea/InputArea';
import * as authService from '../../services/authService';
import * as socketService from '../../services/socketService';
import logo from '../../assets/insurtale-logo.webp';

// API base URL - use empty string for relative URLs in production, localhost for dev
const API_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || '')
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001');

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [portalWelcome, setPortalWelcome] = useState(null);
  const [error, setError] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [messageOffset, setMessageOffset] = useState(0);
  const [whispers, setWhispers] = useState([]); // Story 6: AC6.5, AC6.6 - session-sticky whispers
  const [users, setUsers] = useState([]); // Story 7: AC7.1 - global user list
  const [prefillMessage, setPrefillMessage] = useState(null); // Story 7: Prefill input for whispers

  // AUTO-DISMISS error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    // CHECK if user is already authenticated
    const token = authService.getToken();
    const userData = authService.getUser();

    if (token && userData) {
      setIsAuthenticated(true);
      setUser(userData);
    }
  }, []);

  // FETCH channels on authentication (AC3.3)
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchChannels = async () => {
      try {
        const token = authService.getToken();
        const response = await fetch(`${API_URL}/api/channels`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const channelData = await response.json();
          setChannels(channelData);
        }
      } catch (err) {
        console.error('Failed to fetch channels:', err);
      }
    };

    fetchChannels();
  }, [isAuthenticated]);

  // CONNECT to WebSocket when authenticated (AC2.1)
  useEffect(() => {
    if (!isAuthenticated) return;

    const socket = socketService.connect(
      // ON AUTHENTICATED (AC2.1)
      (data) => {
        setConnected(true);
        setReconnecting(false);
        console.log('WebSocket authenticated', data.user);
      },
      // ON ERROR (AC2.7)
      (error) => {
        console.error('Socket error:', error);
        setError(error.message);

        // HANDLE token errors - logout user
        if (error.code === 'TOKEN_EXPIRED' || error.code === 'INVALID_TOKEN') {
          authService.removeToken();
          setIsAuthenticated(false);
          setUser(null);
        }
      }
    );

    if (socket) {
      // LISTEN for connection status changes
      socket.on('connect', () => {
        setConnected(true);
        setReconnecting(false);
      });

      socket.on('disconnect', () => {
        setConnected(false);
      });

      socket.io.on('reconnect_attempt', () => {
        setReconnecting(true);
      });

      socket.on('reconnect', () => {
        setConnected(true);
        setReconnecting(false);
      });

      // LISTEN for channel-joined events (AC3.2)
      socketService.onChannelJoined((data) => {
        console.log('Joined channel:', data.channel);
        setActiveChannel(data.channel);

        // RESET pagination state for new channel
        setMessageOffset(0);
        setHasMoreMessages(true);

        // LOAD message history for the channel
        loadChannelMessages(data.channel.id);
      });

      // LISTEN for portal welcome message (AC3.7)
      socketService.onPortalWelcome((data) => {
        setPortalWelcome(data.message);
      });

      // LISTEN for messages (AC2.5, AC2.6)
      socketService.onMessage((message) => {
        setMessages((prev) => [...prev, message]);
      });

      // LISTEN for command errors (Story 5)
      socketService.onCommandError((data) => {
        setError(data.error);
      });

      // LISTEN for nick changes (Story 5: AC5.3, AC5.8)
      socketService.onNickChanged((data) => {
        // UPDATE current user if it's their handle that changed
        if (user && user.id === data.user.id) {
          const updatedUser = { ...user, handle: data.newHandle };
          setUser(updatedUser);
          authService.saveUser(updatedUser);
        }
      });

      // LISTEN for system messages (Story 5: AC5.8)
      socketService.onSystemMessage((data) => {
        // ADD system message to messages
        setMessages((prev) => [...prev, {
          id: `system-${Date.now()}`,
          channel_id: activeChannel?.id,
          message_type: 'system',
          content: data.message,
          created_at: data.timestamp
        }]);
      });

      // LISTEN for /help command (Story 5: AC5.5)
      socketService.onShowHelp(() => {
        showHelpMessage();
      });

      // LISTEN for whisper messages (Story 6: AC6.2, AC6.3, AC6.5)
      socketService.onWhisper((whisperData) => {
        // ADD whisper to session-sticky array (AC6.5, AC6.6)
        setWhispers((prev) => [...prev, whisperData]);
      });

      // LISTEN for user list updates (Story 7: AC7.6)
      socketService.onUserListUpdate((data) => {
        setUsers(data.users);
      });

      // LISTEN for message deletion (Story 10: AC10.8)
      socketService.onMessageDeleted((data) => {
        console.log('Message deleted event received:', data);
        // UPDATE the message in the messages array to show deleted content
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === data.messageId
              ? {
                  ...msg,
                  message: '[Message deleted by admin]',
                  message_type: 'deleted',
                  content: '[Message deleted by admin]'
                }
              : msg
          )
        );
      });
    }

    return () => {
      socketService.disconnect();
    };
  }, [isAuthenticated]);

  // LOAD message history for a channel (AC4.1)
  const loadChannelMessages = async (channelId, offset = 0, prepend = false) => {
    try {
      setLoadingHistory(true);
      const token = authService.getToken();
      const response = await fetch(
        `${API_URL}/api/channels/${channelId}/messages?limit=50&offset=${offset}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const channelMessages = await response.json();

        // CHECK if we have more messages (AC4.3)
        if (channelMessages.length < 50) {
          setHasMoreMessages(false);
        }

        if (prepend) {
          // PREPEND older messages for infinite scroll (AC4.2)
          setMessages((prev) => [...channelMessages, ...prev]);
        } else {
          // REPLACE messages on initial load
          setMessages(channelMessages);
          setHasMoreMessages(channelMessages.length === 50);
          setMessageOffset(50);
        }
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // LOAD more messages for infinite scroll (AC4.2)
  const handleLoadMore = async () => {
    if (!activeChannel || loadingHistory || !hasMoreMessages) return;

    const newOffset = messageOffset;
    await loadChannelMessages(activeChannel.id, newOffset, true);
    setMessageOffset(newOffset + 50);
  };

  const handleLoginSuccess = (data) => {
    // SAVE token and user data
    authService.saveToken(data.token);
    authService.saveUser(data.user);

    setIsAuthenticated(true);
    setUser(data.user);
  };

  const handleChannelClick = (channel) => {
    // JOIN the channel via WebSocket (AC3.4)
    socketService.joinChannel(channel.name);
  };

  // HANDLE user click - prefill whisper command (Story 7 UX enhancement)
  const handleUserClick = (clickedUser) => {
    // PREFILL input with whisper command
    setPrefillMessage(`/w ${clickedUser.handle} `);
  };

  // HANDLE logout
  const handleLogout = () => {
    authService.removeToken();
    socketService.disconnect();
    setIsAuthenticated(false);
    setUser(null);
    setConnected(false);
    setChannels([]);
    setActiveChannel(null);
    setMessages([]);
    setWhispers([]);
    setUsers([]);
  };

  // HANDLE message deletion (Story 10: AC10.2)
  const handleDeleteMessage = async (messageId) => {
    try {
      const token = authService.getToken();
      const response = await fetch(`${API_URL}/api/admin/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete message');
      }

      console.log('Message deleted successfully');
      // Message will be updated via WebSocket 'message-deleted' event
    } catch (err) {
      console.error('Error deleting message:', err);
      setError(err.message || 'Failed to delete message');
    }
  };

  // SHOW help message (Story 5: AC5.5)
  const showHelpMessage = () => {
    const helpText = `*** Available commands:
***   /join #channel - Join a channel
***   /w username msg - Whisper (direct message)
***   /me action - Action message
***   /nick newname - Change your handle
***   /afk [reason] - Mark yourself away
***   /back - Return from AFK
***   /help - Show this help message`;

    setMessages((prev) => [...prev, {
      id: `help-${Date.now()}`,
      channel_id: activeChannel?.id,
      message_type: 'system',
      content: helpText,
      created_at: new Date()
    }]);
  };

  const handleSendMessage = (message) => {
    // CLEAR prefill after message sent
    setPrefillMessage(null);

    if (!activeChannel) {
      setError('No active channel');
      return;
    }

    // CHECK if message is a command (starts with /)
    if (message.startsWith('/')) {
      const firstLineBreak = message.indexOf('\n');
      let command, args, restOfMessage;

      if (firstLineBreak !== -1) {
        // MULTILINE command (e.g., /code with multiple lines)
        const firstLine = message.substring(0, firstLineBreak);
        [command, ...args] = firstLine.slice(1).split(' ');
        restOfMessage = message.substring(firstLineBreak + 1);
      } else {
        // SINGLE line command
        [command, ...args] = message.slice(1).split(' ');
        restOfMessage = '';
      }

      // HANDLE client-side only commands
      switch (command.toLowerCase()) {
        case 'code':
          // HANDLE /code command (AC4.9 - user-friendly alternative to backticks)
          const language = args[0] || '';
          let codeContent = args.slice(1).join(' ');

          // IF multiline, use content from next lines
          if (restOfMessage) {
            codeContent = restOfMessage;
          }

          if (!codeContent.trim()) {
            setError('Usage: /code [language] <code> or /code [language] then Shift+Enter for multiline');
            return;
          }

          // CONVERT to Markdown code block
          const codeBlock = `\`\`\`${language}\n${codeContent}\n\`\`\``;

          // SEND as regular message (it will be parsed as Markdown)
          socketService.sendMessage(activeChannel.name, codeBlock, (err) => {
            setError(err.message);
          });
          return;

        case 'help':
          // HANDLE /help locally (Story 5: AC5.5)
          showHelpMessage();
          return;

        default:
          // SEND all other commands to server (Story 5: AC5.1, AC5.2-5.4)
          socketService.sendCommand(message, activeChannel.name, (err) => {
            setError(err.message);
          });
          return;
      }
    }

    // CHECK if trying to send regular message in read-only channel
    if (activeChannel.is_writable === false) {
      setError(`Cannot send messages in #${activeChannel.name}. Use commands like /join #general`);
      return;
    }

    // SEND regular message via WebSocket (AC2.4)
    socketService.sendMessage(activeChannel.name, message, (err) => {
      setError(err.message);
    });
  };

  if (!isAuthenticated) {
    return <HandleSelection onSuccess={handleLoginSuccess} />;
  }

  // FILTER messages by active channel (AC3.4)
  const channelMessages = messages.filter(
    (msg) => msg.channel_id === activeChannel?.id
  );

  // COMBINE channel messages with whispers (Story 6: AC6.5)
  // Whispers are "sticky" and appear in all channels
  const allMessages = [...channelMessages, ...whispers].sort((a, b) =>
    new Date(a.created_at || a.timestamp) - new Date(b.created_at || b.timestamp)
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerCard}>
          <div className={styles.titleSection}>
            <img src={logo} alt="insur_tale Logo" className={styles.logoImage} />
            <h1 className={styles.logo}>Arcology</h1>
          </div>

          <div className={styles.userSection}>
            <ConnectionStatus connected={connected} reconnecting={reconnecting} />
            <span className={styles.userHandle}>@{user?.handle}</span>
            <button className={styles.logoutButton} onClick={handleLogout} title="Logout">
              Logout
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={16} className={styles.errorIcon} />
          {error}
        </div>
      )}

      <div className={styles.contentWrapper}>
        <div className={styles.contentCard}>
          <UserList users={users} currentUser={user} onUserClick={handleUserClick} />

          <ChannelList
            channels={channels}
            activeChannel={activeChannel}
            onChannelClick={handleChannelClick}
          />

          <div className={styles.chatArea}>
          {activeChannel && (
            <>
              <div className={styles.channelHeader}>
                <h2 className={styles.channelName}>#{activeChannel.name}</h2>
                <p className={styles.channelDescription}>{activeChannel.description}</p>
              </div>

              {activeChannel.name === 'portal' && portalWelcome && (
                <div className={styles.portalWelcome}>
                  <pre>{portalWelcome}</pre>
                </div>
              )}

              <MessageArea
                messages={allMessages}
                currentUser={user}
                users={users}
                channelName={activeChannel.name}
                onLoadMore={handleLoadMore}
                hasMore={hasMoreMessages}
                loading={loadingHistory}
                onDeleteMessage={handleDeleteMessage}
              />
              <InputArea
                onSendMessage={handleSendMessage}
                disabled={!connected}
                readOnlyChannel={activeChannel?.is_writable === false}
                channelName={activeChannel?.name}
                prefillMessage={prefillMessage}
              />
            </>
          )}

          {!activeChannel && (
            <div className={styles.noChannel}>
              <p>Wähle einen Kanal zum Chatten</p>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
